// src/config/themeConfig.js

/**
 * Centralized theme configuration object with light and dark color schemes
 * This configuration defines all color values used throughout the application
 */
export const themeConfig = {
  light: {
    // Primary colors
    primary: '#033D74',
    secondary: '#b0b0b0',
    accent: '#a0a0a0',
    
    // Background and text
    background: '#dfdfdf',
    text: '#011B35',
    
    // Section backgrounds
    section1Bg: 'rgba(113, 184, 142, 0.9)',
    section2Bg: 'rgb(195 164 239 / 90%)',
    section3Bg: '#0057af',
    
    // Body gradient
    bodyGradient: 'linear-gradient(135deg, #d7d7d7ca 20%, #7676765c 50%, #f6f5f580 80%)',
    
    // Hero gradient
    heroGradient: 'linear-gradient(17deg, #b0b0b0 20%, #a0a0a0 50%, #dfdfdf 80%)',
    
    // Navigation
    navBackground: '#033D74',
    navText: '#ffffff',
    
    // Form elements
    inputText: '#ffffff',
    inputLabel: '#ffffff',
    
    // Buttons
    buttonBackground: '#b0b0b0',
    buttonText: '#011B35',
    buttonHover: '#a0a0a0',
    
    // Primary button (newsletter, etc.)
    primaryButtonBackground: '#033D74',
    primaryButtonText: '#ffffff',
    primaryButtonHover: '#a0a0a0',
    
    // Links
    linkColor: '#033D74',
    linkHover: '#a0a0a0',
    
    // Hamburger menu
    hamburgerColor: '#ffffff',
    hamburgerCloseColor: '#033D74',
    
    // Shadows and effects
    boxShadow: '0 4px 5px rgba(0, 0, 0, 0.2), 0 -4px 5px rgba(0, 0, 0, 0.2)',
    textShadow: '0 0 1px black'
  },
  
  dark: {
    // Primary colors - adjusted for dark theme
    primary: '#4A90E2',
    secondary: '#404040',
    accent: '#606060',
    
    // Background and text - inverted for dark theme
    background: '#1a1a1a',
    text: '#e0e0e0',
    
    // Section backgrounds - darker variants
    section1Bg: 'rgba(60, 100, 80, 0.9)',
    section2Bg: 'rgb(80 60 120 / 90%)',
    section3Bg: '#2d4a7a',
    
    // Body gradient - dark theme
    bodyGradient: 'linear-gradient(135deg, #2a2a2aca 20%, #1e1e1e5c 50%, #2c2c2c80 80%)',
    
    // Hero gradient - dark theme
    heroGradient: 'linear-gradient(17deg, #404040 20%, #606060 50%, #1a1a1a 80%)',
    
    // Navigation - darker but still visible
    navBackground: '#2d4a7a',
    navText: '#e0e0e0',
    
    // Form elements - light text on dark background
    inputText: '#e0e0e0',
    inputLabel: '#e0e0e0',
    
    // Buttons - dark theme appropriate
    buttonBackground: '#404040',
    buttonText: '#e0e0e0',
    buttonHover: '#606060',
    
    // Primary button - using primary color
    primaryButtonBackground: '#4A90E2',
    primaryButtonText: '#ffffff',
    primaryButtonHover: '#606060',
    
    // Links - using primary color
    linkColor: '#4A90E2',
    linkHover: '#606060',
    
    // Hamburger menu - light colors for visibility
    hamburgerColor: '#e0e0e0',
    hamburgerCloseColor: '#4A90E2',
    
    // Shadows and effects - adjusted for dark theme
    boxShadow: '0 4px 5px rgba(0, 0, 0, 0.4), 0 -4px 5px rgba(0, 0, 0, 0.4)',
    textShadow: '0 0 1px rgba(255, 255, 255, 0.3)'
  }
};

/**
 * CSS custom property names mapping
 * This ensures consistent naming across the application
 */
export const cssVariables = {
  // Primary colors
  primary: '--primary-color',
  secondary: '--secondary-color',
  accent: '--accent-color',
  
  // Background and text
  background: '--background-color',
  text: '--text-color',
  
  // Section backgrounds
  section1Bg: '--section1-bg',
  section2Bg: '--section2-bg',
  section3Bg: '--section3-bg',
  
  // Body gradient
  bodyGradient: '--body-gradient',
  
  // Hero gradient
  heroGradient: '--hero-gradient',
  
  // Navigation
  navBackground: '--nav-background',
  navText: '--nav-text',
  
  // Form elements
  inputText: '--input-text',
  inputLabel: '--input-label',
  
  // Buttons
  buttonBackground: '--button-background',
  buttonText: '--button-text',
  buttonHover: '--button-hover',
  
  // Primary button
  primaryButtonBackground: '--primary-button-background',
  primaryButtonText: '--primary-button-text',
  primaryButtonHover: '--primary-button-hover',
  
  // Links
  linkColor: '--link-color',
  linkHover: '--link-hover',
  
  // Hamburger menu
  hamburgerColor: '--hamburger-color',
  hamburgerCloseColor: '--hamburger-close-color',
  
  // Shadows and effects
  boxShadow: '--box-shadow',
  textShadow: '--text-shadow'
};

/**
 * Helper function to apply theme variables to CSS custom properties
 * @param {string} theme - 'light' or 'dark'
 * @returns {object} CSS custom properties object
 */
export const getCSSVariables = (theme) => {
  const themeColors = themeConfig[theme];
  const cssProps = {};
  
  Object.keys(themeColors).forEach(key => {
    const cssVar = cssVariables[key];
    if (cssVar) {
      cssProps[cssVar] = themeColors[key];
    }
  });
  
  return cssProps;
};